/** Elements whose children OOXML allows only as block content (paragraphs, tables…), never runs. */
const BLOCK_CONTAINERS = new Set(['w:body', 'w:tc', 'w:hdr', 'w:ftr', 'w:footnote', 'w:endnote', 'w:comment', 'w:txbxContent']);

// Comments, CDATA and processing instructions are consumed whole so that markup
// quoted inside them is never read as structure; quoted attribute values may hold `>`.
// Sticky and tried only where a `<` stands: a `<` that opens no token ends the
// scan, where a global scan retried it at every later position. On a part ending
// in unterminated tags that retry was quadratic (200 KB of `<w:r ` held the gate
// 21 s, 2026-10-16). XML forbids `<` in a tag, so a tag never reads past the next one.
const TAG = /<!--[\s\S]*?-->|<!\[CDATA\[[\s\S]*?\]\]>|<\?[\s\S]*?\?>|<\/([^\s<>]+)\s*>|<([^\s<>/!?]+)((?:[^<>"']|"[^<"]*"|'[^<']*')*)>/y;

const XML_WHITESPACE = /^[ \t\r\n]*$/;

interface Span { start: number; end: number }

interface OpenElement {
  readonly name: string;
  readonly start: number;
  /** A `w:r` whose parent is a block container. */
  readonly stray: boolean;
  /** Stray runs directly in this element not yet wrapped; a following stray sibling may extend it. */
  pending: Span | null;
}

/**
 * Wraps in a paragraph every run (`w:r`) placed directly in a block container.
 *
 * Why: Word refuses the whole package ("Word experienced an error trying to
 * open the file") when a run sits outside any paragraph, although ZIP and XML
 * checks pass. Measured in Word for Mac on 2026-10-15: SuperDoc 2.15.1 had
 * exported an empty run directly in `w:body`, between two paragraphs, after a
 * series of agent edits; the gate passed it as valid, so the filed document
 * and every copy were unreadable. The same file opens with that run wrapped in
 * `w:p`; a run directly in `w:tc` or in `w:hdr` is refused the same way, and a
 * run in `w:tc` opens once wrapped. `w:ftr`, `w:footnote`, `w:endnote`,
 * `w:comment` and `w:txbxContent` are inferred, not measured: OOXML gives them
 * the same block-only content model.
 *
 * Wrapping keeps every byte of the run, so no text, property or revision is
 * dropped. Stray sibling runs separated only by whitespace were inline
 * neighbours and share one paragraph. A run inside a paragraph or inside one of
 * its inline containers (`w:hyperlink`, `w:ins`, `w:sdtContent`, `w:fldSimple`…)
 * is untouched; a `w:txbxContent` in a paragraph's drawing is a block container
 * of its own.
 *
 * Like the other string-level repairs, this matches the literal `w:` prefix
 * SuperDoc writes. Markup that does not tokenize or does not nest leaves the XML
 * unchanged, for the strict parser and the other repairs to judge.
 *
 * @param xml - A WordprocessingML part
 * @returns The part with each run of stray siblings wrapped in one `w:p`, and the number of paragraphs inserted
 */
export function repairRunsOutsideParagraph(xml: string): { xml: string; count: number } {
  const open: OpenElement[] = [];
  const wraps: Span[] = [];
  const flush = (element: OpenElement): void => {
    if (element.pending) wraps.push(element.pending);
    element.pending = null;
  };
  const addStray = (container: OpenElement, run: Span): void => {
    if (container.pending && XML_WHITESPACE.test(xml.slice(container.pending.end, run.start))) {
      container.pending.end = run.end;
      return;
    }
    flush(container);
    container.pending = run;
  };

  for (let at = xml.indexOf('<'); at !== -1;) {
    TAG.lastIndex = at;
    const match = TAG.exec(xml);
    if (!match) return { xml, count: 0 };
    const [token, closing, opening, attributes = ''] = match;
    const end = at + token.length;
    if (closing !== undefined) {
      const element = open.pop();
      if (element?.name !== closing) return { xml, count: 0 };
      flush(element);
      const container = open.at(-1);
      if (element.stray && container) addStray(container, { start: element.start, end });
    } else if (opening !== undefined) {
      const container = open.at(-1);
      const stray = opening === 'w:r' && container !== undefined && BLOCK_CONTAINERS.has(container.name);
      if (!attributes.endsWith('/')) open.push({ name: opening, start: at, stray, pending: null });
      else if (stray) addStray(container, { start: at, end });
    }
    at = xml.indexOf('<', end);
  }
  if (open.length || !wraps.length) return { xml, count: 0 };

  const edits = wraps
    .flatMap(({ start, end }) => [{ at: start, text: '<w:p>' }, { at: end, text: '</w:p>' }])
    .sort((left, right) => left.at - right.at);
  const pieces: string[] = [];
  let cursor = 0;
  for (const { at, text } of edits) {
    pieces.push(xml.slice(cursor, at), text);
    cursor = at;
  }
  pieces.push(xml.slice(cursor));
  return { xml: pieces.join(''), count: wraps.length };
}
